import { getAuth } from "firebase/auth";

/** A connected AI agent, as returned by the MCP Worker's agents API. */
export type BreadConnectedAgent = {
  grantId: string;
  clientName: string;
  clientDomain: string | null;
  /** ISO 8601 */
  connectedAt: string;
};

export class AgentsApiError extends Error {
  constructor(
    message: string,
    /** The HTTP status, or null if the request never got a response. */
    readonly status: number | null,
  ) {
    super(message);
    this.name = "AgentsApiError";
  }
}

/** The connector URL users paste into their AI app. */
export function getMcpConnectorUrl(): string {
  return process.env.REACT_APP_MCP_URL ?? "";
}

// The agents API lives on the same Worker as the connector, beside /mcp.
function agentsApiBaseUrl(): string {
  return getMcpConnectorUrl().replace(/\/mcp\/?$/, "");
}

async function authorizedRequest(
  path: string,
  method: "GET" | "DELETE",
  fetchFn: typeof fetch,
): Promise<Response> {
  const user = getAuth().currentUser;
  if (!user) {
    throw new AgentsApiError("Not signed in.", 401);
  }
  const idToken = await user.getIdToken();
  try {
    return await fetchFn(`${agentsApiBaseUrl()}${path}`, {
      method,
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch (error) {
    throw new AgentsApiError(
      error instanceof Error ? error.message : "Network request failed.",
      null,
    );
  }
}

export async function listConnectedAgents(
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<BreadConnectedAgent[]> {
  const response = await authorizedRequest("/api/agents", "GET", fetchFn);
  if (!response.ok) {
    throw new AgentsApiError(
      `Listing agents failed (${response.status}).`,
      response.status,
    );
  }
  const body = (await response.json()) as { agents: BreadConnectedAgent[] };
  return body.agents;
}

/** Revokes an agent's access. An agent that's already gone counts as success. */
export async function disconnectAgent(
  grantId: string,
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<void> {
  const response = await authorizedRequest(
    `/api/agents/${encodeURIComponent(grantId)}`,
    "DELETE",
    fetchFn,
  );
  if (!response.ok && response.status !== 404) {
    throw new AgentsApiError(
      `Disconnecting the agent failed (${response.status}).`,
      response.status,
    );
  }
}
